import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import { apolloClientInternal } from '@getaccept/lib-shared/src/internal/apollo-client-internal';
import type { DashboardPerformer } from '../../../types/dashboard-performer';
import { DashboardQueries } from '../graphql/dashboard.queries';
import type { DocumentStats } from '../types/document-stats';
import type { DocumentStatsType } from '../types/document-stats-type';

export class DashboardService {
  static async loadDocumentStatsByType(
    type: DocumentStatsType,
    showAll: boolean
  ): Promise<DocumentStats> {
    const {
      data: { documentStatsByType },
    } = await apolloClientInternal.query({
      query: DashboardQueries.documentsByType,
      variables: {
        type,
        showAll,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return documentStatsByType;
  }

  static async loadDocumentHits(
    signDateInterval: { from: number; to: number },
    showAll: boolean
  ): Promise<DocumentStats> {
    const {
      data: { documentHits },
    } = await apolloClientInternal.query({
      query: DashboardQueries.documentHits,
      variables: {
        signDateInterval,
        showAll,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return documentHits;
  }

  static async loadTopPerformers(dateInterval: {
    from: number;
    to: number;
  }): Promise<DashboardPerformer[]> {
    const {
      data: { topPerformers },
    } = await apolloClientInternal.query({
      query: DashboardQueries.topPerformers,
      variables: {
        dateInterval,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return topPerformers.map(performer => ({
      ...performer,
      count: performer.signedNrOfDocuments,
      value: performer.valueOfDocuments,
      total: performer.totalNrOfDocuments,
    }));
  }

  static async loadDocumentDealTimes(
    signDateInterval: { from: number; to: number },
    showAll: boolean
  ): Promise<{ hours: number; days: number }> {
    const {
      data: { documentDealTimes },
    } = await apolloClientInternal.query({
      query: DashboardQueries.documentDealTimes,
      variables: {
        signDateInterval,
        showAll,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return documentDealTimes;
  }

  static async loadEstimateClosingDeals(
    avgCloseDays: number,
    showAll: boolean
  ): Promise<DocumentStats> {
    const {
      data: { estimateClosingDeals },
    } = await apolloClientInternal.query({
      query: DashboardQueries.estimateClosingDeals,
      variables: {
        avgCloseDays,
        showAll,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return estimateClosingDeals;
  }
}
